import Modal from "@/components/Modal";
import SkeletonRow from "@/components/SkeletonRow";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import useSessionStorage from "@/hooks/useSessionStorage";
import { formatRupiah } from "@/lib/utils";
import { getListDataAset, getListJenisAset } from "@/services/getData";
import { DataAset, JenisAset } from "@/types";
import { useQuery } from "@tanstack/react-query";
import {
  ChevronDown,
  ChevronRight,
  Edit,
  FileText,
  Home,
  Info,
  Plus,
  Trash2,
  Zap,
} from "lucide-react"; // Gunakan Lucide icons
import { Fragment, useState } from "react";
import Form from "./Form";

export default function Aset({
  idDataPendaftaran,
  isValidPermanen,
}: {
  idDataPendaftaran: string;
  isValidPermanen: boolean;
}) {
  const [sessionPeriode] = useSessionStorage("sessionPeriode", {
    id_sys_periode: "",
  });

  const [showEdit, setShowEdit] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [showDetail, setShowDetail] = useState(false);
  const [showInsert, setShowInsert] = useState(false);
  const [showPdf, setShowPdf] = useState("");
  const [selectedRow, setSelectedRow] = useState<DataAset | null>(null);

  const { data, isPending } = useQuery({
    queryKey: ["listDataAset"],
    queryFn: () => getListDataAset(sessionPeriode?.id_sys_periode || ""),
  });

  const { data: dataJenisAset } = useQuery({
    queryKey: ["listJenisAset"],
    queryFn: () => getListJenisAset(),
  });

  const dataJenisAsetWajib = dataJenisAset?.filter((item: JenisAset) => {
    return item.is_wajib;
  });

  return (
    <div className="space-y-6 animate-in fade-in duration-500">
      {/* --- HEADER --- */}
      <div>
        <h1 className="text-2xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
          <Home className="w-6 h-6 text-blue-600 dark:text-blue-400" />
          Data Aset & Tempat Tinggal
        </h1>
        <p className="text-slate-500 dark:text-slate-400 text-sm mt-1">
          Lengkapi informasi kepemilikan aset dan kondisi tempat tinggal.
        </p>
      </div>

      {/* --- ALERT INFO --- */}
      <Alert className="bg-amber-50 border-amber-200 dark:bg-amber-900/20 dark:border-amber-800 text-amber-900 dark:text-amber-200">
        <Info className="h-5 w-5 text-amber-600 dark:text-amber-400" />
        <AlertTitle className="font-bold text-amber-800 dark:text-amber-300">
          Petunjuk Pengisian
        </AlertTitle>
        <AlertDescription className="text-sm mt-2 space-y-2">
          <p>
            Pastikan data aset diisi dengan lengkap dan jujur. Data ini akan
            divalidasi dengan dokumen pendukung (PBB/Rekening Listrik/Air).
          </p>
          <div className="flex flex-wrap gap-2 mt-2">
            <span className="text-xs font-semibold uppercase tracking-wider text-amber-800/70 dark:text-amber-200/70">
              Wajib Diisi:
            </span>
            {dataJenisAsetWajib?.map((item: JenisAset) => (
              <Badge
                key={item.id_ref_aset_jenis}
                variant="outline"
                className="bg-white dark:bg-slate-900 text-amber-700 dark:text-amber-300 border-amber-300 dark:border-amber-700"
              >
                {item.nama_jenis_aset}
              </Badge>
            ))}
          </div>
        </AlertDescription>
      </Alert>

      {/* --- ACTION BAR --- */}
      <div className="flex justify-between items-center">
        <h2 className="text-lg font-semibold text-slate-800 dark:text-slate-200">
          Daftar Aset
        </h2>
        {!isValidPermanen && (
          <Button onClick={() => setShowInsert(true)} size="sm" className="">
            <Plus className="w-4 h-4 mr-2" /> Tambah Aset
          </Button>
        )}
      </div>

      {/* --- TABLE CONTENT --- */}
      {isPending ? (
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <SkeletonRow key={i} />
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-sm overflow-hidden">
          <div className="overflow-x-auto w-full">
            <table className="w-full text-sm text-left">
              <thead className="text-xs text-slate-500 uppercase bg-slate-50 dark:bg-slate-950 dark:text-slate-400 border-b border-slate-200 dark:border-slate-800">
                <tr>
                  <th className="px-4 py-3 w-10"></th>
                  <th className="px-4 py-3">Jenis Aset</th>
                  <th className="px-4 py-3">Listrik (VA)</th>
                  <th className="px-4 py-3">Tagihan Air</th>
                  <th className="px-4 py-3">Luas Tanah</th>
                  <th className="px-4 py-3">Luas Bangunan</th>
                  <th className="px-4 py-3 text-center">Berkas</th>
                  <th className="px-4 py-3 w-10"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                {data?.length > 0 ? (
                  data.map((row: DataAset) => (
                    <Fragment key={row.id_data_aset}>
                      <tr
                        className={`transition-colors ${
                          showDetail &&
                          selectedRow?.id_data_aset === row.id_data_aset
                            ? "bg-slate-50 dark:bg-slate-800/50"
                            : "hover:bg-slate-50 dark:hover:bg-slate-800/30"
                        }`}
                      >
                        <td className="px-4 py-3 text-center">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-6 w-6 p-0 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200"
                            onClick={() => {
                              setShowDetail(
                                showDetail &&
                                  selectedRow?.id_data_aset === row.id_data_aset
                                  ? false
                                  : true,
                              );
                              setSelectedRow(row);
                            }}
                          >
                            {showDetail &&
                            selectedRow?.id_data_aset === row.id_data_aset ? (
                              <ChevronDown className="w-4 h-4" />
                            ) : (
                              <ChevronRight className="w-4 h-4" />
                            )}
                          </Button>
                        </td>
                        <td className="px-4 py-3 font-medium text-slate-900 dark:text-slate-200">
                          {row.jenis_aset?.nama_jenis_aset}
                        </td>
                        <td className="px-4 py-3 text-slate-600 dark:text-slate-400">
                          {row.tegangan_listrik?.tegangan_listrik} VA
                        </td>
                        <td className="px-4 py-3 text-slate-600 dark:text-slate-400 font-mono">
                          {formatRupiah(Number(row.pembayaran_air_old))}
                        </td>
                        <td className="px-4 py-3 text-slate-600 dark:text-slate-400">
                          {row?.aset_luas_tanah_old} m²
                        </td>
                        <td className="px-4 py-3 text-slate-600 dark:text-slate-400">
                          {row?.aset_luas_bangunan_old} m²
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex justify-center gap-2">
                            {row.path_berkas_listrik && (
                              <Button
                                variant="outline"
                                size="icon"
                                className="h-8 w-8 text-yellow-600 border-yellow-200 bg-yellow-50 hover:bg-yellow-100 dark:bg-yellow-900/20 dark:border-yellow-800 dark:text-yellow-400"
                                onClick={() => {
                                  setShowPdf("path_berkas_listrik");
                                  setSelectedRow(row);
                                }}
                                title="Berkas Listrik"
                              >
                                <Zap className="w-4 h-4" />
                              </Button>
                            )}
                            {row.path_berkas_air && (
                              <Button
                                variant="outline"
                                size="icon"
                                className="h-8 w-8 text-cyan-600 border-cyan-200 bg-cyan-50 hover:bg-cyan-100 dark:bg-cyan-900/20 dark:border-cyan-800 dark:text-cyan-400"
                                onClick={() => {
                                  setShowPdf("path_berkas_air");
                                  setSelectedRow(row);
                                }}
                                title="Berkas Air"
                              >
                                <span className="font-bold text-xs">AIR</span>
                              </Button>
                            )}
                            {row.path_berkas_pbb && (
                              <Button
                                variant="outline"
                                size="icon"
                                className="h-8 w-8 text-emerald-600 border-emerald-200 bg-emerald-50 hover:bg-emerald-100 dark:bg-emerald-900/20 dark:border-emerald-800 dark:text-emerald-400"
                                onClick={() => {
                                  setShowPdf("path_berkas_pbb");
                                  setSelectedRow(row);
                                }}
                                title="Berkas PBB"
                              >
                                <FileText className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-right">
                          {!isValidPermanen && (
                            <div className="flex justify-end gap-1">
                              <Button
                                size="icon"
                                variant="ghost"
                                className="h-8 w-8"
                                onClick={() => {
                                  setShowEdit(true);
                                  setSelectedRow(row);
                                }}
                              >
                                <Edit className="w-4 h-4" />
                              </Button>
                              <Button
                                size="icon"
                                variant="ghost"
                                className="h-8 w-8 text-red-600 dark:text-red-400"
                                onClick={() => {
                                  setShowDelete(true);
                                  setSelectedRow(row);
                                }}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          )}
                          {/* {!isValidPermanen && (
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-8 w-8 p-0"
                                >
                                  <MoreHorizontal className="w-4 h-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent
                                align="end"
                                className="dark:bg-slate-900 dark:border-slate-800"
                              >
                                <DropdownMenuLabel>Opsi</DropdownMenuLabel>
                                <DropdownMenuSeparator className="dark:bg-slate-800" />
                                <DropdownMenuItem
                                  onClick={() => {
                                    setShowEdit(true);
                                    setSelectedRow(row);
                                  }}
                                >
                                  <Edit className="w-4 h-4 mr-2" /> Edit
                                </DropdownMenuItem>
                                <DropdownMenuItem
                                  onClick={() => {
                                    setShowDelete(true);
                                    setSelectedRow(row);
                                  }}
                                  className="text-red-600 focus:text-red-600 dark:text-red-400"
                                >
                                  <Trash2 className="w-4 h-4 mr-2" /> Hapus
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          )} */}
                        </td>
                      </tr>

                      {/* EXPANDABLE DETAIL ROW */}
                      {showDetail &&
                        selectedRow?.id_data_aset === row.id_data_aset && (
                          <tr className="bg-slate-50 dark:bg-slate-950">
                            <td
                              colSpan={8}
                              className="p-4 border-b border-slate-200 dark:border-slate-800"
                            >
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4 text-sm animate-in slide-in-from-top-2">
                                <div>
                                  <h4 className="text-xs font-bold uppercase text-slate-500 mb-2 tracking-wider">
                                    Detail Kepemilikan
                                  </h4>
                                  <div className="flex justify-between border-b border-slate-200 dark:border-slate-800 py-2">
                                    <span className="text-slate-600 dark:text-slate-400">
                                      Status
                                    </span>
                                    <span className="font-medium text-slate-900 dark:text-slate-200">
                                      {row.kepemilikan_aset?.nama_kepemilikan ||
                                        "-"}
                                    </span>
                                  </div>
                                </div>
                                <div>
                                  <h4 className="text-xs font-bold uppercase text-slate-500 mb-2 tracking-wider">
                                    Sumber Daya
                                  </h4>
                                  <div className="flex justify-between border-b border-slate-200 dark:border-slate-800 py-2">
                                    <span className="text-slate-600 dark:text-slate-400">
                                      Sumber Air
                                    </span>
                                    <span className="font-medium text-slate-900 dark:text-slate-200">
                                      {row.sumber_air?.sumber_air || "-"}
                                    </span>
                                  </div>
                                  <div className="flex justify-between border-b border-slate-200 dark:border-slate-800 py-2">
                                    <span className="text-slate-600 dark:text-slate-400">
                                      Sumber Listrik
                                    </span>
                                    <span className="font-medium text-slate-900 dark:text-slate-200">
                                      {row.sumber_listrik?.sumber_listrik ||
                                        "-"}
                                    </span>
                                  </div>
                                </div>
                              </div>
                              {!isValidPermanen && (
                                <div className="flex justify-start gap-1">
                                  <Button
                                    size="icon"
                                    variant="ghost"
                                    className="h-8 w-8"
                                    onClick={() => {
                                      setShowEdit(true);
                                      setSelectedRow(row);
                                    }}
                                  >
                                    <Edit className="w-4 h-4" />
                                  </Button>
                                  <Button
                                    size="icon"
                                    variant="ghost"
                                    className="h-8 w-8 text-red-600 dark:text-red-400"
                                    onClick={() => {
                                      setShowDelete(true);
                                      setSelectedRow(row);
                                    }}
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                </div>
                              )}
                            </td>
                          </tr>
                        )}
                    </Fragment>
                  ))
                ) : (
                  <tr>
                    <td
                      colSpan={8}
                      className="px-4 py-12 text-center text-slate-500 dark:text-slate-400"
                    >
                      <div className="flex flex-col items-center justify-center gap-3">
                        <Home className="w-10 h-10 text-slate-300 dark:text-slate-600" />
                        <p>Belum ada data aset yang ditambahkan.</p>
                        {!isValidPermanen && (
                          <Button
                            variant="outline"
                            onClick={() => setShowInsert(true)}
                          >
                            Tambah Aset Sekarang
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* --- MODALS --- */}
      {/* Insert Modal */}
      <Modal
        show={showInsert}
        onClose={() => setShowInsert(false)}
        className="lg:w-[70vw]"
      >
        <Form
          method="POST"
          closeModal={() => setShowInsert(false)}
          idDataPendaftaran={idDataPendaftaran}
        />
      </Modal>

      {/* Edit Modal */}
      <Modal
        show={showEdit}
        onClose={() => setShowEdit(false)}
        className="lg:w-[70vw]"
      >
        <Form
          method="PATCH"
          closeModal={() => {
            setShowEdit(false);
            setShowDetail(false);
          }}
          idDataPendaftaran={idDataPendaftaran}
          selectedRow={selectedRow}
        />
      </Modal>

      {/* Delete Modal */}
      <Modal show={showDelete} onClose={() => setShowDelete(false)}>
        <Form
          method="DELETE"
          closeModal={() => setShowDelete(false)}
          selectedRow={selectedRow}
        />
      </Modal>

      {/* PDF Preview Modal */}
      <Modal
        show={showPdf.length > 0}
        onClose={() => setShowPdf("")}
        className="h-[85vh] w-[95vw] lg:w-[60vw]"
      >
        <iframe
          src={`${selectedRow?.[showPdf as keyof DataAset]}`}
          title="Preview Berkas"
          className="absolute h-full w-full rounded-lg bg-slate-100"
        />
      </Modal>
    </div>
  );
}
